import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import { Button, Stack } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useCreateNote } from '../../../sic-lib/api/hooks/useNotes';
import { ContactNoteStatus } from '../../../sic-lib/enums/ContactNoteStatus';
import { GenericReducer, genericReducer } from '../../../sic-lib/reducers/genericReducer';
import AxiosErrorAlert from '../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../Shared/ButtonLoading';

interface ComponentState {
    noteText: string;
    noteStatus: ContactNoteStatus;
}

interface Props {
    contactId: number;
    onToggleHandler: () => void;
}

export default function TextFieldContactNoteCreate({ contactId, onToggleHandler }: Props) {
    const { classes } = useStyles();

    const {
        mutateAsync: createNote,
        error,
        isLoading,
        isSuccess,
        isError,
    } = useCreateNote(contactId);

    const [compState, compDispatch] = React.useReducer<GenericReducer<ComponentState>>(
        genericReducer,
        {
            noteText: '',
            noteStatus: ContactNoteStatus.Active,
        },
    );

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        compDispatch({ noteText: event.target.value });
    };

    const handleChangePin = () => {
        compState.noteStatus === ContactNoteStatus.Pinned
            ? compDispatch({ noteStatus: ContactNoteStatus.Active })
            : compDispatch({ noteStatus: ContactNoteStatus.Pinned });
    };

    const handleSubmit = async () => {
        await createNote({
            contactId: contactId,
            text: compState.noteText,
            noteStatus: compState.noteStatus,
        });

        onToggleHandler();
    };

    return (
        <Paper
            className={
                compState.noteStatus === ContactNoteStatus.Pinned
                    ? classes.paperPinned
                    : classes.paper
            }
        >
            <Stack spacing={1}>
                <InputBase
                    id="text_create_note"
                    name="text"
                    error={!!error}
                    value={compState.noteText}
                    onChange={handleChange}
                    placeholder="Type your note here..."
                    fullWidth
                    multiline
                    minRows={6}
                    maxRows={8}
                    autoFocus
                />
                <AxiosErrorAlert error={error} />
                <Stack direction="row" spacing={1} justifyContent="space-between">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={compState.noteStatus === ContactNoteStatus.Pinned}
                                onChange={handleChangePin}
                                color="primary"
                                size="small"
                            />
                        }
                        label="Pinned"
                    />
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                            aria-label="Cancel"
                            size="small"
                            onClick={onToggleHandler}
                            variant="outlined"
                            startIcon={<CloseIcon />}
                        >
                            Cancel
                        </Button>
                        <ButtonLoading
                            aria-label="Save"
                            onClick={handleSubmit}
                            color="success"
                            variant="outlined"
                            size="small"
                            startIcon={<DoneIcon />}
                            isLoading={isLoading}
                            isSuccess={isSuccess}
                            isError={isError}
                        >
                            Save
                        </ButtonLoading>
                    </Stack>
                </Stack>
            </Stack>
        </Paper>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    paper: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.noteStatus.main,
    },
    paperPinned: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.noteStatus.light,
    },
    btnSave: {
        color: theme.palette.conversationTimelinePaper.light,
    },
}));
